// src/pages/TermsAndConditions.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const LAST_UPDATED = 'April 2, 2026';
const COMPANY      = 'Aucwini Technology LLP';
const BRAND        = 'Caryugo';
const EMAIL        = 'legal@caryugo.com';
const ADDRESS      = 'Indore, Madhya Pradesh, India';

const Section = ({ id, title, children }) => (
  <section className="lp-section" id={id}>
    <h2 className="lp-section-title">{title}</h2>
    <div className="lp-section-body">{children}</div>
  </section>
);

const TermsAndConditions = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="lp-page">
      {/* Hero */}
      <div className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-badge">Legal</div>
          <h1 className="lp-hero-title">Terms &amp; Conditions</h1>
          <p className="lp-hero-meta">Last updated: {LAST_UPDATED}</p>
          <p className="lp-hero-sub">
            Please read these terms carefully before using the {BRAND} platform.
            By accessing or using our services, you agree to be bound by these terms.
          </p>
        </div>
      </div>

      <div className="lp-layout">
        {/* Sidebar TOC */}
        <aside className="lp-sidebar">
          <div className="lp-toc">
            <p className="lp-toc-label">Contents</p>
            <nav>
              {[
                ['#acceptance',      '1. Acceptance of Terms'],
                ['#services',        '2. Our Services'],
                ['#eligibility',     '3. Eligibility'],
                ['#account',         '4. User Account'],
                ['#buying',          '5. Buying a Vehicle'],
                ['#selling',         '6. Selling a Vehicle'],
                ['#payments',        '7. Payments & Fees'],
                ['#prohibited',      '8. Prohibited Conduct'],
                ['#ip',              '9. Intellectual Property'],
                ['#disclaimers',     '10. Disclaimers'],
                ['#liability',       '11. Limitation of Liability'],
                ['#indemnification', '12. Indemnification'],
                ['#termination',     '13. Termination'],
                ['#governing',       '14. Governing Law'],
                ['#contact',         '15. Contact Us'],
              ].map(([href, label]) => (
                <a key={href} href={href} className="lp-toc-link">{label}</a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <main className="lp-main">
          <Section id="acceptance" title="1. Acceptance of Terms">
            <p>
              These Terms and Conditions ("Terms") constitute a legally binding agreement between
              you ("User", "you", or "your") and <strong>{COMPANY}</strong> ("Company", "we", "us",
              or "our"), the owner and operator of the <strong>{BRAND}</strong> platform accessible
              at caryugo.com and through our mobile applications.
            </p>
            <p>
              By registering an account, browsing, or using any part of our platform, you
              acknowledge that you have read, understood, and agree to be bound by these Terms
              and our Privacy Policy. If you do not agree, please discontinue use immediately.
            </p>
          </Section>

          <Section id="services" title="2. Our Services">
            <p>
              {BRAND} is an online marketplace that facilitates the buying and selling of
              pre-owned motor vehicles in India. Our platform provides:
            </p>
            <ul className="lp-list">
              <li>A searchable inventory of certified pre-owned vehicles listed by verified sellers.</li>
              <li>Vehicle inspection and valuation services through our network of certified inspectors.</li>
              <li>Test drive scheduling and booking services.</li>
              <li>Financing assistance and EMI calculation tools (in partnership with third-party lenders).</li>
              <li>Documentation and registration transfer guidance.</li>
              <li>Seller tools to list, price, and manage vehicle listings.</li>
            </ul>
            <p>
              {BRAND} acts as an intermediary marketplace. We are not a dealer, broker, or
              financial institution. The final sale contract is between the buyer and seller.
            </p>
          </Section>

          <Section id="eligibility" title="3. Eligibility">
            <p>
              To use our platform you must:
            </p>
            <ul className="lp-list">
              <li>Be at least 18 years of age.</li>
              <li>Be a citizen or legal resident of India.</li>
              <li>Possess a valid government-issued identity proof.</li>
              <li>Have the legal authority to enter into binding contracts.</li>
              <li>Not be barred from receiving services under applicable Indian law.</li>
            </ul>
            <p>
              By using this platform you represent and warrant that you meet all eligibility
              requirements. We reserve the right to verify your identity at any time.
            </p>
          </Section>

          <Section id="account" title="4. User Account">
            <p>
              When you register on {BRAND}, you agree to:
            </p>
            <ul className="lp-list">
              <li>Provide accurate, current, and complete information during registration.</li>
              <li>Maintain and promptly update your account information.</li>
              <li>Keep your OTP and session credentials confidential.</li>
              <li>Accept responsibility for all activities under your account.</li>
              <li>Notify us immediately at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> of any unauthorized use.</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these Terms,
              provide false information, or engage in fraudulent activity.
            </p>
          </Section>

          <Section id="buying" title="5. Buying a Vehicle">
            <p>
              When purchasing a vehicle through {BRAND}:
            </p>
            <ul className="lp-list">
              <li>
                <strong>Inspection Reports:</strong> Our inspection reports are prepared in good faith by
                trained inspectors. They are advisory in nature and do not constitute a warranty.
              </li>
              <li>
                <strong>Test Drives:</strong> Test drives are subject to slot availability and require a
                valid driving licence. You assume responsibility for any damage during a test drive.
              </li>
              <li>
                <strong>RC Transfer:</strong> Transfer of the Registration Certificate (RC) is governed
                by the Motor Vehicles Act, 1988. We assist but are not liable for delays caused by
                government authorities.
              </li>
              <li>
                <strong>Financing:</strong> Loan approvals are at the sole discretion of our lending
                partners. {BRAND} does not guarantee any financing offer.
              </li>
              <li>
                <strong>No Returns:</strong> Used vehicle sales are final unless the vehicle has been
                materially misrepresented. Raise disputes within 48 hours of delivery.
              </li>
            </ul>
          </Section>

          <Section id="selling" title="6. Selling a Vehicle">
            <p>When listing a vehicle for sale on {BRAND}, you agree that:</p>
            <ul className="lp-list">
              <li>You are the legal owner of the vehicle or have explicit authority to sell it.</li>
              <li>All information provided about the vehicle (registration, mileage, condition, accident history) is accurate and complete.</li>
              <li>The vehicle is free from any financial encumbrance (hypothecation discharged) unless disclosed.</li>
              <li>You will not list stolen, seized, or court-attached vehicles.</li>
              <li>Misrepresentation of a vehicle may result in immediate listing removal, account suspension, and legal action.</li>
              <li>{BRAND} may charge a platform fee or commission upon successful sale as disclosed at the time of listing.</li>
            </ul>
          </Section>

          <Section id="payments" title="7. Payments & Fees">
            <p>
              All transactions on {BRAND} are processed through secure, RBI-compliant payment gateways.
            </p>
            <ul className="lp-list">
              <li>Booking amounts and token payments are non-refundable unless the sale falls through due to seller fault or material misrepresentation.</li>
              <li>Platform fees, if applicable, are displayed transparently before payment confirmation.</li>
              <li>GST and other applicable taxes are charged in addition to stated prices as required by law.</li>
              <li>Refunds, where applicable, are processed within 7–14 business days.</li>
              <li>We are not responsible for payment failures caused by your bank or payment provider.</li>
            </ul>
          </Section>

          <Section id="prohibited" title="8. Prohibited Conduct">
            <p>You agree not to:</p>
            <ul className="lp-list">
              <li>Post false, misleading, or fraudulent vehicle listings.</li>
              <li>Conduct transactions outside the platform to avoid fees (circumvention).</li>
              <li>Scrape, crawl, or harvest data from our platform without written consent.</li>
              <li>Attempt to hack, reverse-engineer, or disrupt our systems.</li>
              <li>Use our platform to facilitate money laundering or illegal activities.</li>
              <li>Harass, threaten, or abuse other users or {BRAND} staff.</li>
              <li>Create multiple accounts to exploit offers or evade restrictions.</li>
              <li>Upload content that is defamatory, obscene, or infringes third-party rights.</li>
            </ul>
            <p>
              Violation of this section may result in immediate account termination and referral
              to law enforcement under the Indian Penal Code and Information Technology Act, 2000.
            </p>
          </Section>

          <Section id="ip" title="9. Intellectual Property">
            <p>
              All content on the {BRAND} platform — including logos, trademarks, software code,
              databases, design elements, text, and graphics — is the exclusive property of
              {COMPANY} or its licensors and is protected under the Copyright Act, 1957,
              the Trade Marks Act, 1999, and applicable international treaties.
            </p>
            <p>
              You are granted a limited, non-exclusive, revocable licence to access and use the
              platform solely for personal, non-commercial purposes. You may not reproduce,
              distribute, or create derivative works without our prior written consent.
            </p>
          </Section>

          <Section id="disclaimers" title="10. Disclaimers">
            <p>
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              {BRAND} does not warrant that: (a) the platform will be uninterrupted or error-free;
              (b) vehicle information provided by sellers is accurate; (c) inspection reports are
              exhaustive; or (d) financing will be approved.
            </p>
          </Section>

          <Section id="liability" title="11. Limitation of Liability">
            <p>
              To the maximum extent permitted by applicable law, {COMPANY} shall not be liable for
              any indirect, incidental, special, consequential, or punitive damages, including loss
              of profits, data, goodwill, or other intangible losses, resulting from your use of
              or inability to use the platform.
            </p>
            <p>
              In no event shall our aggregate liability to you exceed the amount paid by you to
              {BRAND} in the six (6) months preceding the event giving rise to the claim.
            </p>
          </Section>

          <Section id="indemnification" title="12. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless {COMPANY}, its partners, officers,
              employees, agents, and service providers from any claims, liabilities, damages,
              judgments, losses, costs, or expenses (including reasonable legal fees) arising out of
              or related to: (a) your use of the platform; (b) your breach of these Terms;
              (c) your violation of any applicable law; or (d) your vehicle listing or transaction.
            </p>
          </Section>

          <Section id="termination" title="13. Termination">
            <p>
              We may suspend or terminate your access to the platform at our sole discretion,
              without notice, for conduct that we believe violates these Terms or is harmful to
              other users, us, or third parties, or for any other reason.
            </p>
            <p>
              You may deactivate your account at any time by contacting us at{' '}
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. Termination does not relieve you of
              obligations incurred prior to termination.
            </p>
          </Section>

          <Section id="governing" title="14. Governing Law & Dispute Resolution">
            <p>
              These Terms are governed by and construed in accordance with the laws of India.
              Any dispute arising from or relating to these Terms shall be subject to the exclusive
              jurisdiction of the courts located in <strong>Indore, Madhya Pradesh, India</strong>.
            </p>
            <p>
              We encourage you to contact us first to resolve disputes amicably. For unresolved
              disputes, arbitration may be initiated under the Arbitration and Conciliation Act, 1996.
            </p>
          </Section>

          <Section id="contact" title="15. Contact Us">
            <div className="lp-contact-card">
              <div className="lp-contact-row">
                <span className="lp-contact-icon">🏢</span>
                <div>
                  <strong>{COMPANY}</strong><br />
                  {ADDRESS}
                </div>
              </div>
              <div className="lp-contact-row">
                <span className="lp-contact-icon">✉️</span>
                <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              </div>
            </div>
          </Section>

          {/* Footer nav */}
          <div className="lp-footer-nav">
            <Link to="/privacy" className="lp-footer-link">Privacy Policy →</Link>
            <Link to="/"        className="lp-footer-link">← Back to Home</Link>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TermsAndConditions;